<template>
  <div class="p-4 max-w-xl mx-auto" v-if="!isLoading">
    <!-- Back Button -->
    <div
      @click="$router.back()"
      class="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 cursor-pointer shadow transition"
    >
      <BackSvg />
    </div>

    <!-- Header -->
    <div class="py-6 flex flex-col items-center">
      <h2 class="text-2xl font-semibold text-zinc-700 text-center">
        Security Settings
      </h2>
      <p class="text-sm text-gray-500 mt-1 text-center">
        Manage camera and microphone permissions for this device
      </p>
    </div>

    <!-- Permission Card -->
    <div class="bg-white shadow rounded-lg p-4 mb-4">
      <h3 class="text-lg font-medium mb-3">Device Permissions</h3>
      <div class="flex justify-between items-center mb-2">
        <span>Camera</span>
        <span
          :class="{
            'text-green-600 font-semibold': cameraStatus === 'granted',
            'text-yellow-600 font-semibold': cameraStatus === 'prompt',
            'text-red-600 font-semibold': cameraStatus === 'denied',
          }"
        >
          {{ cameraStatus }}
        </span>
      </div>
      <div class="flex justify-between items-center mb-2">
        <span>Microphone</span>
        <span
          :class="{
            'text-green-600 font-semibold': micStatus === 'granted',
            'text-yellow-600 font-semibold': micStatus === 'prompt',
            'text-red-600 font-semibold': micStatus === 'denied',
          }"
        >
          {{ micStatus }}
        </span>
      </div>

      <!-- Action Buttons -->
      <div class="flex space-x-2 mt-4">
        <button
          @click="checkPermissions"
          class="flex-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition"
        >
          Refresh Status
        </button>
        <button
          @click="requestMedia"
          class="flex-1 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded transition"
        >
          Enable Camera & Mic
        </button>
      </div>
    </div>

    <!-- Video Preview -->
    <div v-if="stream" class="mb-4 flex justify-center">
      <video
        ref="videoRef"
        autoplay
        playsinline
        muted
        class="border border-gray-300 rounded-lg w-80 h-60"
      ></video>
    </div>

    <!-- Error Message -->
    <p v-if="errorMessage" class="text-red-500 mb-2">{{ errorMessage }}</p>

    <!-- Help Box for Denied Permissions -->
    <div
      v-if="cameraStatus === 'denied' || micStatus === 'denied'"
      class="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 rounded"
    >
      <p class="font-semibold mb-2">Permissions Blocked</p>
      <p class="text-sm mb-2">
        Camera or Microphone is blocked. Enable it manually in your browser
        settings.
      </p>
      <button
        @click="showHelp"
        class="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm transition"
      >
        How to Enable
      </button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from "vue";
import BackSvg from "@/components/svgs/BackSvg.vue";

export default {
  name: "CameraMicPermissions",
  components: { BackSvg },
  setup() {
    const cameraStatus = ref("unknown");
    const micStatus = ref("unknown");
    const stream = ref(null);
    const errorMessage = ref("");
    const videoRef = ref(null);

    const checkPermissions = async () => {
      if (!navigator.permissions) {
        errorMessage.value = "Permissions API not supported in this browser.";
        return;
      }
      try {
        const camera = await navigator.permissions.query({ name: "camera" });
        const mic = await navigator.permissions.query({ name: "microphone" });
        cameraStatus.value = camera.state;
        micStatus.value = mic.state;
        camera.onchange = () => (cameraStatus.value = camera.state);
        mic.onchange = () => (micStatus.value = mic.state);
      } catch (err) {
        errorMessage.value = "Error checking permissions: " + err.message;
      }
    };

    const requestMedia = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        stream.value = mediaStream;
        errorMessage.value = "";
        if (videoRef.value) videoRef.value.srcObject = mediaStream;
      } catch (err) {
        errorMessage.value = "Access denied or error: " + err.message;
      }
    };

    const showHelp = () => {
      alert(
        "To re-enable camera/mic access:\n\n" +
          "Chrome / Edge / Brave:\n1. Click the lock icon next to the URL.\n2. Site settings → Allow Camera & Microphone.\n3. Refresh the page.\n\n" +
          "Firefox:\n1. Click the lock icon → Permissions → Allow Camera & Microphone.\n2. Refresh the page.\n\n" +
          "Safari:\n1. Safari menu → Settings for This Website → Allow Camera & Microphone.\n2. Refresh the page."
      );
    };

    watch(stream, (newStream) => {
      if (videoRef.value && newStream) videoRef.value.srcObject = newStream;
    });

    onMounted(() => checkPermissions());

    return {
      cameraStatus,
      micStatus,
      stream,
      errorMessage,
      videoRef,
      checkPermissions,
      requestMedia,
      showHelp,
    };
  },
};
</script>

<style scoped>
button {
  cursor: pointer;
}
</style>
